// Bausteine des Rechnungsformulars ohne Oberfläche: Zustand von Kunde, Positionen und Skonto, Eingaben lesen, rechnen
// wie der Server (server/src/modules/rechnungstool/data.ts) und den Body fürs Schreiben bauen. Benutzt von
// RechnungForm.tsx, KundeWahl.tsx, Positionen.tsx, ZahlungBlock.tsx (und später Angebote und wiederkehrende Rechnungen).
import { istDrittland, istEuAusland, landName as landNameWelt } from './laender-welt';
import type { Kunde, KundenArt, Position, Rate, Skonto } from './types';
import { einheit } from './waehrung';

// Kundenart im Formular: Firma im EU-Ausland ist eine Firma mit anderem Land; Kunde außerhalb der EU eine Firma oder
// Privatperson mit einem Land außerhalb der EU (welt)
export type Art = KundenArt | 'eu' | 'welt';
export const KUNDEN_ARTEN: [Art, string][] = [
  ['firma', 'Firma'],
  ['privat', 'Privatperson'],
  ['behoerde', 'Behörde'],
  ['eu', 'Firma im EU-Ausland'],
  ['welt', 'Kunde außerhalb der EU'],
];

// Kunde im Formular (alles als Text, wie getippt). kundeId: aus der Kundenliste gewählt (sonst null: neuer Kunde, der
// Server legt ihn beim Schreiben in der Liste an oder findet ihn nach Name und PLZ)
export interface KundeWerte {
  kundeId: string | null;
  art: Art;
  name: string;
  extra: string;
  street: string;
  zip: string;
  city: string;
  // nur bei 'eu' (Land im EU-Ausland)
  country: string;
  // nur bei 'welt': Land außerhalb der EU und ob Privatperson (sonst Firma)
  land: string;
  weltPrivat: boolean;
  // USt-IdNr. (bei 'welt': Steuernummer des Kunden, optional)
  vatId: string;
  // E-Mail und Leitweg-ID (für XRechnung)
  email: string;
  buyerRef: string;
}

export const LEERER_KUNDE: KundeWerte = {
  kundeId: null,
  art: 'firma',
  name: '',
  extra: '',
  street: '',
  zip: '',
  city: '',
  country: 'AT',
  land: 'CH',
  weltPrivat: false,
  vatId: '',
  email: '',
  buyerRef: '',
};

export const artOf = (k: Pick<Kunde, 'country' | 'art'>): Art =>
  !k.country || k.country === 'DE' ? (k.art ?? 'firma') : istDrittland(k.country) ? 'welt' : 'eu';

// Kunde der Liste (oder Anschrift aus einer alten Rechnung) → Formular. kundeId: Kunde der Liste dahinter
export const kundeWerteOf = (k: Kunde, kundeId: string | null = k.id ?? null): KundeWerte => ({
  kundeId,
  art: artOf(k),
  name: k.name,
  extra: k.extra ?? '',
  street: k.street,
  zip: k.zip,
  city: k.city,
  country: istEuAusland(k.country) ? k.country! : 'AT',
  land: istDrittland(k.country) ? k.country! : 'CH',
  weltPrivat: istDrittland(k.country) && k.art === 'privat',
  vatId: k.vatId ?? '',
  email: k.email ?? '',
  buyerRef: k.buyerRef ?? '',
});

// Formular → customer im Body. E-Mail und Leitweg-ID nur bei XRechnung (sonst nicht sichtbar, also auch nicht
// mitschicken)
export function customerBody(w: KundeWerte, o: { xrechnung: boolean }) {
  const firma = w.art === 'firma' || w.art === 'eu' || (w.art === 'welt' && !w.weltPrivat);
  return {
    name: w.name,
    extra: w.extra,
    street: w.street,
    zip: w.zip,
    city: w.city,
    country: w.art === 'eu' ? w.country : w.art === 'welt' ? w.land : 'DE',
    art: w.art === 'eu' ? 'firma' : w.art === 'welt' ? (w.weltPrivat ? 'privat' : 'firma') : w.art,
    vatId: firma ? w.vatId : '',
    email: o.xrechnung ? w.email : '',
    buyerRef: o.xrechnung ? w.buyerRef : '',
  };
}

// Kunde außerhalb der EU im Formular: 'firma' oder 'privat' (Vorschläge für den Hinweis bei 0 %), sonst null
export const drittlandArt = (w: Pick<KundeWerte, 'art' | 'weltPrivat'>): 'firma' | 'privat' | null =>
  w.art === 'welt' ? (w.weltPrivat ? 'privat' : 'firma') : null;

// Steuersätze wie bei Lexware: 19, 7 und 0 % (bei 0 % schreibt der Mandant den Hinweis selbst, was er bedeutet,
// erkennt der Server); Kleinunternehmer ohne. Beim Kunden außerhalb der EU ebenso alle drei.
export const ratesFor = (ku: boolean): Rate[] => (ku ? [19] : [19, 7, 0]);

export const RATE_LABEL: Record<string, string> = { '19': '19 %', '7': '7 %', '0': '0 %' };
export const RATE_SUM: Record<string, string> = { '0': '0 % USt' };
export const UNITS = ['Stück', 'Std.', 'Tag', 'Pauschal', 'm²', 'm', 'kg', 'Monat'];

// Steuer einer gespeicherten Position (Rechnung, Angebot, Vorlage, Entwurf, Leistung) fürs Formular: „13b“ und „eu“
// (Reverse charge EU) von früher sind jetzt 0 % (der Hinweis dazu kommt aus hinweisAus); Unbekanntes: undefined
export const rateAus = (v: unknown): Rate | undefined =>
  v === 19 || v === 7 || v === 0 ? v : v === '13b' || v === 'eu' || v === '0' ? 0 : undefined;

// Satz, der bisher auf Rechnungen mit „§ 13b“ stand: Hinweis für alte Stände ohne eigenen Hinweis
export const HINWEIS_13B = 'Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG)';
// ebenso für alte Stände mit Reverse charge EU (ohne „13b“, damit es bei Kunden im EU-Ausland bleibt, wie auf dem
// Server data.ts HINWEIS_RC)
export const HINWEIS_RC = 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)';
// Kunde außerhalb der EU: Vorschläge für den Hinweis bei 0 % (wie auf dem Server data.ts und steuerhinweis.ts
// DRITTLAND_HINWEISE)
export const HINWEIS_DRITTLAND = 'Nicht im Inland steuerbare Leistung. Die Umsatzsteuer schuldet der Leistungsempfänger.';
export const HINWEIS_NICHT_STEUERBAR = 'Nicht im Inland steuerbare Leistung.';
export const HINWEIS_AUSFUHR = 'Steuerfreie Ausfuhrlieferung.';
export const DRITTLAND_HINWEISE: Record<'firma' | 'privat', [string, string][]> = {
  firma: [
    ['Leistung an Firma im Ausland', HINWEIS_DRITTLAND],
    ['Ware ins Ausland', HINWEIS_AUSFUHR],
  ],
  privat: [
    ['Leistung im Ausland', HINWEIS_NICHT_STEUERBAR],
    ['Ware ins Ausland', HINWEIS_AUSFUHR],
  ],
};
const NENNT_13B = /(^|[^\d])13\s*b\b/iu;

// Meint der Hinweis § 13b? Wie ist13b auf dem Server (data.ts): „13b“ oder die üblichen Sätze dazu
const P13B =
  /(^|[^\d])13\s*b\b|steuerschuldner|steuerschuldumkehr|umkehr(ung)?\s+der\s+steuerschuld|steuerschuld(?!ner)[^.]{0,40}leistungsempf|leistungsempf(ä|ae)nger[^.]{0,30}schuldet|schuldet[^.]{0,30}leistungsempf(ä|ae)nger|reverse[\s-]*charge/iu;
export const ist13b = (hinweis: string | null | undefined) => !!hinweis && P13B.test(hinweis);

// Hinweis bei 0 % USt aus einem gespeicherten Stand: sein eigener; hat eine Position noch „13b“ oder „eu“ und meint der
// eigene nicht die Steuerschuld des Kunden (z. B. steuerfrei zu einer anderen Position), kommt der bisherige Satz dazu
// (sonst ginge sie still verloren). Fehlt er und hat eine Position noch „13b“, der zuletzt benutzte Hinweis der Firma
// (Overview.hinweis0), wenn er § 13b meint, sonst der bisherige Satz; ebenso bei „eu“ (der gemerkte nur, wenn er nicht
// ausdrücklich „13b“ nennt). Sonst beim Kunden außerhalb der EU, der eine Firma ist (drittland 'firma'), der Satz zur
// nicht steuerbaren Leistung, sonst leer. Wie steuerhinweis.ts auf dem Server (hinweisFuerFormular, mitSteuerschuld).
export function hinweisAus(
  items: readonly { rate?: unknown }[],
  hinweis: unknown,
  hinweis0: string | null | undefined,
  drittland: 'firma' | 'privat' | null = null,
): string {
  const alt13b = items.some((i) => i.rate === '13b');
  const altEu = items.some((i) => i.rate === 'eu');
  if (typeof hinweis === 'string' && hinweis.trim()) {
    const satz = alt13b ? HINWEIS_13B : altEu ? HINWEIS_RC : null;
    if (!satz || ist13b(hinweis)) return hinweis;
    const t = hinweis.trim();
    return `${t}${/[.!?]$/.test(t) ? '' : '.'} ${satz}`;
  }
  if (alt13b) return hinweis0 && ist13b(hinweis0) ? hinweis0 : HINWEIS_13B;
  if (altEu) return hinweis0 && ist13b(hinweis0) && !NENNT_13B.test(hinweis0) ? hinweis0 : HINWEIS_RC;
  return drittland === 'firma' ? HINWEIS_DRITTLAND : '';
}

// Position im Formular (alles als Text, wie getippt)
export interface PosRow {
  key: number;
  description: string;
  quantity: string;
  unit: string;
  // Einzelpreis netto, bis 4 Nachkommastellen
  price: string;
  rate: Rate;
  // Rabatt in Prozent (leer: kein Rabatt)
  discount: string;
  // von der KI vorgeschlagen (gelb), bis der Mandant die Zeile ändert
  ki?: boolean;
  // aus einer alten Rechnung vorgeschlagen: Hinweis daneben, woher der Preis kommt
  tip?: string;
  // vom Mandanten geändert (dann ersetzt ein neuer Vorschlag die Zeile nicht mehr)
  touched?: boolean;
  // aus einer Leistung übernommen (Lager: bucht diesen Artikel ab, wenn er Bestand führt; nur unsichtbar mitgeschickt)
  artikelId?: string;
}

let nextKey = 1;
export const emptyRow = (): PosRow => ({ key: nextKey++, description: '', quantity: '1', unit: 'Stück', price: '', rate: 19, discount: '' });
// Position (Vorlage, Verlauf, Entwurf) → Zeile
export const rowOf = (p: Partial<Position>, extra: Partial<PosRow> = {}): PosRow => ({
  ...emptyRow(),
  description: p.description ?? '',
  quantity: typeof p.quantity === 'number' ? deQty(p.quantity) : '',
  unit: p.unit ?? '',
  price: typeof p.price === 'number' ? dePrice(p.price) : '',
  rate: rateAus(p.rate) ?? 19,
  discount: p.discount ? deProzent(p.discount) : '',
  ...(p.artikelId ? { artikelId: p.artikelId } : {}),
  ...extra,
});
// Zeile nur vorgeschlagen und unverändert, oder ganz leer: darf durch einen neuen Vorschlag ersetzt werden
export const free = (r: PosRow) => (r.tip ? !r.touched : !r.description.trim() && !r.price.trim());

export const deQty = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 3, useGrouping: false });
// Preis mit 2 bis 4 Nachkommastellen (0,085 bleibt 0,085, 40 wird 40,00)
export const dePrice = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4, useGrouping: false });
export const deProzent = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2, useGrouping: false });
// Betrag mit Preis-Stellen: „0,085 €“, „45,00 €“, „45,00 USD“
export const eurPrice = (n: number, w = 'EUR') => `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${einheit(w)}`;

// Menge: „2,5“ oder „2.5“, bis zu drei Nachkommastellen
export function parseQty(text: string): number | undefined {
  const t = text.trim().replace(',', '.');
  return /^\d+(\.\d{1,3})?$/.test(t) && Number(t) > 0 ? Number(t) : undefined;
}

// Preis: „0,085“, „1.234,5678“, „45“ (bis 4 Nachkommastellen); leer → null, ungültig → undefined. „1.234“ ist wie
// sonst Tausend, „0.085“ aber 0,085 (Tausender beginnen nie mit 0).
export function parsePrice(text: string): number | null | undefined {
  let t = text.replace(/\s|€/g, '');
  if (!t) return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  else if (/^[1-9]\d{0,2}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
  if (!/^\d+(\.\d{1,4})?$/.test(t)) return undefined;
  return Number(t);
}

// Prozent (Rabatt, Skonto): „10“, „12,5“, bis 2 Nachkommastellen; leer → null, ungültig → undefined
export function parseProzent(text: string): number | null | undefined {
  const t = text.replace(/\s|%/g, '').replace(',', '.');
  if (!t) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return undefined;
  return Number(t);
}

// Positionsbetrag in Cent wie auf dem Server (lineCents): Menge × Preis × (1 − Rabatt) in ganzen Zahlen, kaufmännisch
// gerundet (1234,5 m² × 0,085 € = 104,93 €)
export function lineCents(quantity: number, price: number, discount = 0): number {
  const q = BigInt(Math.round(quantity * 1000));
  const p = BigInt(Math.round(price * 10000));
  const d = BigInt(10000 - Math.round(discount * 100));
  // Menge × 1000, Preis × 10000 (Euro), Rabatt-Faktor × 10000 → Cent: / (1000 × 10000 × 10000 / 100)
  const den = 1000n * 10000n * 100n;
  return Number((q * p * d + den / 2n) / den);
}

// Betrag einer Zeile in Cent (null: Menge, Preis oder Rabatt noch nicht lesbar)
export function rowCents(r: PosRow): number | null {
  const q = parseQty(r.quantity);
  const p = parsePrice(r.price);
  const d = parseProzent(r.discount);
  if (q === undefined || typeof p !== 'number' || d === undefined || (d ?? 0) > 100) return null;
  return lineCents(q, p, d ?? 0);
}

export interface Summen {
  lines: (number | null)[];
  // netto je Steuerart (19, 7, 0), in der Reihenfolge der Rechnung
  groups: [string, number][];
  net: number;
  vat: number;
  gross: number;
}
const ORDER = ['19', '7', '0'];
export const vatOf = (key: string, n: number) => (key === '19' || key === '7' ? Math.round((n * Number(key)) / 100) : 0);

// Summen wie auf dem Server: Umsatzsteuer je Steuerart auf die Summe netto dieser Art (Kleinunternehmer: ohne)
export function summen(rows: PosRow[], ku: boolean): Summen {
  const lines = rows.map(rowCents);
  const byRate = new Map<string, number>();
  rows.forEach((r, i) => {
    const key = String(r.rate);
    byRate.set(key, (byRate.get(key) ?? 0) + (lines[i] ?? 0));
  });
  const net = lines.reduce<number>((s, l) => s + (l ?? 0), 0);
  const groups = ku ? [] : [...byRate].sort((a, b) => ORDER.indexOf(a[0]) - ORDER.indexOf(b[0]));
  const vat = groups.reduce((s, [key, n]) => s + vatOf(key, n), 0);
  return { lines, groups, net, vat, gross: net + vat };
}

// Zeile → Position im Body (ungültige Zahlen als NaN, dann meldet der Server, was fehlt). Kleinunternehmer: ohne
// Umsatzsteuer, die Steuerart spielt keine Rolle. ohnePreise (Lieferschein ohne Preise): Preis und Rabatt nur, wenn
// lesbar und der Preis über 0 (die Spalten sind nicht zu sehen), sonst ohne Preis (null)
export function positionBody(r: PosRow, ku: boolean, o: { ohnePreise?: boolean } = {}) {
  const d = parseProzent(r.discount);
  const p = parsePrice(r.price);
  const artikel = r.artikelId ? { artikelId: r.artikelId } : {};
  if (o.ohnePreise) {
    return {
      description: r.description,
      quantity: parseQty(r.quantity) ?? NaN,
      unit: r.unit,
      price: typeof p === 'number' && p > 0 ? p : null,
      rate: ku ? 19 : r.rate,
      ...(typeof d === 'number' && d <= 100 ? { discount: d } : {}),
      ...artikel,
    };
  }
  return {
    description: r.description,
    quantity: parseQty(r.quantity) ?? NaN,
    unit: r.unit,
    price: p ?? NaN,
    rate: ku ? 19 : r.rate,
    ...(d === null ? {} : { discount: d ?? NaN }),
    ...artikel,
  };
}

// Skonto im Formular: Prozent und Tage als Text; null: kein Skonto
export interface SkontoWerte {
  prozent: string;
  tage: string;
}
export const skontoWerteOf = (s: Skonto | null | undefined): SkontoWerte | null =>
  s ? { prozent: deProzent(s.prozent), tage: String(s.tage) } : null;
export function skontoBody(s: SkontoWerte | null) {
  if (!s) return null;
  const p = parseProzent(s.prozent);
  const t = s.tage.trim();
  return { prozent: typeof p === 'number' ? p : NaN, tage: /^\d+$/.test(t) ? Number(t) : NaN };
}

// Skonto wie auf dem Server (skontoOf): Abzug kaufmännisch gerundet vom Bruttobetrag, gilt bis Rechnungsdatum + Tage
export function skontoBetrag(s: Skonto | null | undefined, invoiceDate: string, grossCents: number) {
  if (!s || grossCents <= 0) return null;
  const cents = Number((BigInt(grossCents) * BigInt(Math.round(s.prozent * 100)) + 5000n) / 10000n);
  const bis = new Date(`${invoiceDate}T00:00:00Z`);
  bis.setUTCDate(bis.getUTCDate() + s.tage);
  return { bis: bis.toISOString().slice(0, 10), cents, zahlen: grossCents - cents };
}

// Datum ('YYYY-MM-DD') plus n Tage
export const plusTage = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};

// Tage von a bis b ('YYYY-MM-DD')
export const tageZwischen = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

export const landName = (code: string) => landNameWelt(code);
